/**
 * useKpiChannel — subscribes to `kpi:<restaurant_id>` for live KPI updates, with polling
 * fallback. Mirrors useFeedChannel's degradation strategy.
 */
import { useEffect, useRef, useState } from "react";
import { Socket } from "phoenix";
import type { Channel } from "phoenix";
import { config } from "@/api/config";
import { api } from "@/api/client";
import { getSocketToken } from "@/api/session";
import type { KpiValue, KpiUpdateMessage } from "@/types/contract";
import type { ConnectionState } from "@/hooks/useFeedChannel";

const POLL_INTERVAL_MS = 30_000;

export function useKpiChannel(
  restaurantId: string,
  onKpis: (kpis: KpiValue[]) => void,
): ConnectionState {
  const [state, setState] = useState<ConnectionState>("connecting");
  const cb = useRef(onKpis);
  cb.current = onKpis;

  useEffect(() => {
    let socket: Socket | null = null;
    let channel: Channel | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let disposed = false;

    const startPolling = () => {
      if (pollTimer || disposed) return;
      setState((s) => (s === "live" ? s : "polling"));
      const tick = async () => {
        try {
          const kpis = await api.getKpis({ restaurant_id: restaurantId });
          if (!disposed) {
            cb.current(kpis);
            setState("polling");
          }
        } catch {
          if (!disposed) setState("offline");
        }
      };
      void tick();
      pollTimer = setInterval(tick, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
    };

    if (config.useMocks || !config.wsUrl) {
      startPolling();
      return () => {
        disposed = true;
        stopPolling();
      };
    }

    try {
      // Токен сокета: socket_token из /session (SSO Rover) либо dev-токен из env.
      const token = getSocketToken();
      socket = new Socket(config.wsUrl, {
        params: token ? { token } : {},
      });
      socket.onError(() => {
        if (!disposed) startPolling();
      });
      socket.onClose(() => {
        if (!disposed) startPolling();
      });
      socket.connect();

      channel = socket.channel(`kpi:${restaurantId}`, {});
      channel.on("kpi_update", (msg: KpiUpdateMessage) => cb.current(msg.kpis));
      channel
        .join()
        .receive("ok", () => {
          if (disposed) return;
          stopPolling();
          setState("live");
        })
        .receive("error", () => !disposed && startPolling())
        .receive("timeout", () => !disposed && startPolling());
    } catch {
      startPolling();
    }

    return () => {
      disposed = true;
      stopPolling();
      if (channel) channel.leave();
      if (socket) socket.disconnect();
    };
  }, [restaurantId]);

  return state;
}
